import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import { UniversalUpgradeProduct } from '@airship-platform/airship-proposal';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  initState,
  pricebookUniversalUpgradeProductsReducer,
  PricebookUniversalUpgradeProductsState,
} from './pricebookUniversalUpgradeProducts.reducer';

interface PricebookUniversalUpgradeProductsProviderContextProps {
  pricebookUniversalUpgradeProducts: UniversalUpgradeProduct[];
  currentPricebookUniversalUpgradeProduct: UniversalUpgradeProduct | null;
  isLoading: boolean;
  isLoaded: boolean;
  fetchPricebookUniversalUpgradeProducts: () => Promise<void>;
}

const PricebookUniversalUpgradeProductsProviderContext = createContext<
  PricebookUniversalUpgradeProductsProviderContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<PricebookUniversalUpgradeProductsState>;
}>;

export const PricebookUniversalUpgradeProductsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    pricebookUniversalUpgradeProductsReducer,
    {
      ...initState,
      ...initialState,
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const pricebookUniversalUpgradeProducts = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids]
  );

  const currentPricebookUniversalUpgradeProduct = useMemo(
    () => state.entities[state.currentPricebookUniversalUpdradeProductId || ''],
    [state.currentPricebookUniversalUpdradeProductId, state.entities]
  );

  const fetchPricebookUniversalUpgradeProducts: PricebookUniversalUpgradeProductsProviderContextProps['fetchPricebookUniversalUpgradeProducts'] =
    useCallback(async () => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_PRICEBOOK_UNIVERSAL_UPGRADE_PRODUCTS' });
      try {
        const pricebookUniversalUpgradeProducts = await httpClient.get<
          UniversalUpgradeProduct[]
        >(`/pricebook-upgrades/products`);
        dispatch({
          type: 'FETCH_PRICEBOOK_UNIVERSAL_UPGRADE_PRODUCTS_SUCCESS',
          payload: { pricebookUniversalUpgradeProducts },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_PRICEBOOK_UNIVERSAL_UPGRADE_PRODUCTS_FAILURE',
          payload: {
            error: `Failed to fetch pricebookUniversalUpdrade: ${error}`,
          },
        });
      }
    }, [httpClient, isOnline]);

  return (
    <PricebookUniversalUpgradeProductsProviderContext.Provider
      value={{
        pricebookUniversalUpgradeProducts,
        currentPricebookUniversalUpgradeProduct,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        fetchPricebookUniversalUpgradeProducts,
      }}
    >
      {children}
    </PricebookUniversalUpgradeProductsProviderContext.Provider>
  );
};

export const usePricebookUniversalUpgradeProducts =
  (): PricebookUniversalUpgradeProductsProviderContextProps => {
    const context = useContext(
      PricebookUniversalUpgradeProductsProviderContext
    );
    if (context === undefined) {
      throw new Error(
        'usePricebookUniversalUpgradeProductsProvider must be used within an PricebookUniversalUpgradeProductsProvider'
      );
    }
    return context;
  };
